import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');

  const categories = [
    { key: 'all', label: 'Todas' },
    { key: 'jogo', label: 'Jogos' },
    { key: 'treino', label: 'Treinos' },
    { key: 'evento', label: 'Eventos' },
    { key: 'geral', label: 'Geral' },
  ];

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news?limit=50`);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredNews = category === 'all' 
    ? news 
    : news.filter(n => n.category === category);

  return (
    <div className="min-h-screen pt-20" data-testid="news-page">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1495555687398-3f50d6e79e1e?auto=format&fit=crop&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 text-center section-padding">
          <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Atualidade</span>
          <h1 className="font-heading text-5xl md:text-7xl font-black mt-2">NOTÍCIAS</h1>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-[#0A0A0A] border-b border-white/10 sticky top-20 z-40">
        <div className="container-custom section-padding">
          <div className="flex flex-wrap gap-2 py-4">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setCategory(cat.key)}
                className={`px-4 py-2 text-xs uppercase tracking-widest font-bold transition-all ${
                  category === cat.key
                    ? 'bg-white text-black'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`category-${cat.key}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-16 section-padding" data-testid="news-grid">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 h-96 skeleton" />
              ))}
            </div>
          ) : filteredNews.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredNews.map((item) => (
                <Link
                  key={item.id}
                  to={`/noticias/${item.id}`}
                  className="news-card group"
                  data-testid={`news-item-${item.id}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={item.image_url || 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&q=80'}
                      alt={item.title}
                      className="news-image w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <span className="text-xs uppercase tracking-widest text-gray-500">
                        {item.category}
                      </span>
                      <div className="flex items-center gap-1 text-gray-500 text-xs">
                        <Calendar size={12} />
                        <span>{new Date(item.created_at).toLocaleDateString('pt-PT')}</span>
                      </div>
                    </div>
                    <h3 className="font-heading text-xl font-bold group-hover:text-gray-300 transition-colors line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 text-sm mt-3 line-clamp-3">{item.content}</p>
                    <div className="mt-4 flex items-center gap-2 text-white group-hover:gap-3 transition-all">
                      <span className="text-xs uppercase tracking-widest font-bold">Ler mais</span>
                      <ChevronRight size={14} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-24 border border-white/10">
              <Calendar size={48} className="mx-auto mb-4 text-gray-600" />
              <p className="text-gray-500">Nenhuma notícia encontrada</p>
              <p className="text-gray-600 text-sm mt-2">As notícias serão publicadas em breve</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsPage;
